import { z } from 'zod'
import type { EtatEnvoi, StatutEnvoi, StatutPiece, TransmissionPiece } from './domaine'
import { indicationDepot } from './pieces'

// Envois photo depuis le téléphone (appli installée). Un envoi, c'est un appui sur
// « Envoyer » : les pages d'un même document, dans l'ordre choisi, qui arrivent en une ou
// plusieurs requêtes (reprises après coupure comprises), puis deviennent une seule pièce.
// Fonctions pures : la route, le service et l'interface en dérivent.

const MO = 1024 * 1024

/**
 * Limites partagées par le téléphone (file d'envoi) et la route : le téléphone ne construit
 * jamais une requête que le serveur refuserait par construction.
 */
export const LIMITES_ENVOI = {
  /** Par fichier : une photo en pleine résolution ou un PDF. */
  octetsParFichier: 25 * MO,
  /** Fichiers par requête (au-delà, le téléphone découpe l'envoi en tranches). */
  fichiersParRequete: 20,
  /** Octets par requête (somme des fichiers). */
  octetsParRequete: 100 * MO,
  /** Pages d'un même envoi. */
  fichiersParEnvoi: 40,
  /**
   * Attente maximale d'une requête à la porte du serveur (envois simultanés bornés) avant la
   * réponse « occupé » : le téléphone l'ajoute à son délai par requête.
   */
  attentePorteMs: 60_000,
} as const

export type NatureFichier = 'jpeg' | 'png' | 'heic' | 'pdf'

/** Nature d'un fichier reçu, d'après son type déclaré puis son extension ; null : refusé. */
export function natureFichier(nom: string, typeMime: string): NatureFichier | null {
  const mime = typeMime.toLowerCase()
  const ext = nom.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? ''
  if (mime === 'image/jpeg' || mime === 'image/jpg' || ext === 'jpg' || ext === 'jpeg')
    return 'jpeg'
  if (mime === 'image/png' || ext === 'png') return 'png'
  if (mime === 'image/heic' || mime === 'image/heif' || ext === 'heic' || ext === 'heif')
    return 'heic'
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  return null
}

/**
 * Clés des fichiers d'une requête (empreinte, ou « refuse:nom:taille ») : une clé répétée dans
 * la requête (même photo choisie deux fois) reçoit un suffixe « #n », pour que le compte des
 * fichiers reçus corresponde aux fichiers envoyés. Une tranche rejouée donne les mêmes clés.
 */
export function clesFichiers(cles: readonly string[]): string[] {
  const vues = new Map<string, number>()
  return cles.map((k) => {
    const n = (vues.get(k) ?? 0) + 1
    vues.set(k, n)
    return n === 1 ? k : `${k}#${n}`
  })
}

/**
 * Délai sans nouvelle tranche au-delà duquel un envoi incomplet est assemblé avec ce qui est
 * arrivé (et passe « à vérifier ») : le reste a pu être abandonné sur le téléphone.
 */
export const SILENCE_ENVOI_MS = 60 * 60_000

export function completudeEnvoi(
  e: { recus: number; attendus: number; derniereActivite: Date },
  maintenant: Date,
): 'complet' | 'attente' | 'silence' {
  if (e.recus >= e.attendus) return 'complet'
  if (e.recus > 0 && maintenant.getTime() - e.derniereActivite.getTime() >= SILENCE_ENVOI_MS)
    return 'silence'
  return 'attente'
}

/**
 * Champs texte d'une requête d'envoi, lus avant les fichiers : identifiant de l'envoi (choisi
 * par le téléphone), nombre total de fichiers de l'envoi, rang de chaque fichier de la requête
 * dans l'envoi (dans l'ordre des fichiers), coins de chaque page recadrée (`lireCadres`).
 */
export const champsEnvoi = z
  .object({
    envoiId: z.uuid(),
    nbFichiers: z.coerce.number().int().min(1).max(LIMITES_ENVOI.fichiersParEnvoi),
    rangs: z
      .string()
      .regex(/^\d{1,3}(,\d{1,3})*$/)
      .transform((t) => t.split(',').map(Number)),
    /** Où va le document (« perso ») : écrite ou dictée avant l'envoi. */
    indication: indicationDepot,
    /** Pages recadrées sur le téléphone (JSON), relues sans jamais bloquer l'envoi. */
    cadres: z.string().max(16_000).optional(),
  })
  .refine((c) => c.rangs.every((r) => r < c.nbFichiers), {
    message: 'Rang hors de l’envoi.',
    path: ['rangs'],
  })
export type ChampsEnvoi = z.infer<typeof champsEnvoi>

/** Avancement de la pièce, seul renseignement donné hors périmètre : en cours ou terminé. */
export function traitementDe(statut: StatutPiece | null): 'en_cours' | 'termine' | null {
  if (statut === null) return null
  return statut === 'recue' || statut === 'extraite' ? 'en_cours' : 'termine'
}

/**
 * État d'un envoi dans « Mes envois ». `piece` n'est renseignée que si la pièce est dans le
 * périmètre de la personne : sinon, une fois traitée, « transmis aux administrateurs », sans
 * détail.
 */
export function etatEnvoi(e: {
  statut: StatutEnvoi
  fichiersRecus: number
  fichiersAttendus: number
  traitement: 'en_cours' | 'termine' | null
  piece: { statut: StatutPiece; rangee: boolean } | null
}): EtatEnvoi {
  if (e.statut === 'echec') return 'a_verifier'
  if (e.statut !== 'assemble' || e.traitement === null) return 'recu'
  if (e.traitement === 'en_cours') return 'en_lecture'
  if (!e.piece) return 'transmis'
  switch (e.piece.statut) {
    case 'rattachee':
      return e.piece.rangee ? 'range' : 'classe'
    case 'a_verifier':
      return 'a_verifier'
    case 'doublon':
      return 'deja_recu'
    case 'ignoree':
      return 'ignore'
    default:
      return 'en_lecture'
  }
}

/** Une étape du suivi d'un document envoyé : faite, en cours ou qui attend quelqu'un. */
export type EtapeDepot = {
  cle: 'recue' | 'lue' | 'rattachee' | 'cabinet'
  etat: 'fait' | 'en_cours' | 'attention'
  texte: string
}

/**
 * Le suivi d'un document envoyé, par quelque voie que ce soit (dépôt, photo) : reçu, lu, rattaché,
 * puis, si l'entité a un cabinet qui les reçoit, déposé chez lui. Le dernier en cours dit ce qui se
 * passe ; `attention` : un humain doit regarder.
 */
export function etapesDepot(p: {
  statut: StatutPiece
  entite: string | null
  transmission: TransmissionPiece | null
  transmiseA: string | null
}): EtapeDepot[] {
  const etapes: EtapeDepot[] = [{ cle: 'recue', etat: 'fait', texte: 'Reçue' }]
  if (p.statut === 'recue') {
    etapes.push({ cle: 'lue', etat: 'en_cours', texte: 'Lecture en cours' })
    return etapes
  }
  etapes.push({ cle: 'lue', etat: 'fait', texte: 'Lue' })
  switch (p.statut) {
    case 'extraite':
      etapes.push({ cle: 'rattachee', etat: 'en_cours', texte: 'Rattachement en cours' })
      return etapes
    case 'a_verifier':
      etapes.push({ cle: 'rattachee', etat: 'attention', texte: 'À vérifier' })
      return etapes
    case 'doublon':
      etapes.push({ cle: 'rattachee', etat: 'attention', texte: 'Déjà reçue' })
      return etapes
    case 'ignoree':
      etapes.push({ cle: 'rattachee', etat: 'attention', texte: 'Écartée' })
      return etapes
    case 'rattachee':
    case 'incluse':
      break
  }
  etapes.push({
    cle: 'rattachee',
    etat: 'fait',
    texte: p.entite ? `Rattachée à ${p.entite}` : 'Rattachée',
  })
  const cabinet = etapeCabinet(p)
  if (cabinet) etapes.push(cabinet)
  return etapes
}

function etapeCabinet(p: {
  transmission: TransmissionPiece | null
  transmiseA: string | null
}): EtapeDepot | null {
  const cle = 'cabinet'
  switch (p.transmission) {
    case 'a_faire':
    case 'en_attente':
      return { cle, etat: 'en_cours', texte: 'À déposer chez le cabinet' }
    case 'faite':
      return { cle, etat: 'fait', texte: p.transmiseA ? `Chez ${p.transmiseA}` : 'Chez le cabinet' }
    case 'a_decider':
      return { cle, etat: 'attention', texte: 'Décision à prendre avant le cabinet' }
    case 'incertain':
      return { cle, etat: 'attention', texte: 'Dépôt à vérifier chez le cabinet' }
    case 'manuelle':
    case 'sans_objet':
    case null:
      return null
  }
}

const JOUR_MS = 86_400_000
/** Copie disque gardée après l'assemblage quand la pièce n'est pas rangée (contenu en base). */
export const CONSERVATION_ASSEMBLE_MS = 7 * JOUR_MS
/** Copie disque gardée après un échec d'assemblage, le temps qu'un humain regarde. */
export const CONSERVATION_ECHEC_MS = 30 * JOUR_MS

/**
 * Les fichiers d'un envoi peuvent-ils quitter le disque ? Dès que la pièce est rangée ; sinon
 * sept jours après l'assemblage, trente après un échec ; jamais pendant la réception.
 */
export function nettoyable(
  e: { statut: StatutEnvoi; rangee: boolean; assembleLe: Date | null; recuLe: Date },
  maintenant: Date,
): boolean {
  const age = (d: Date) => maintenant.getTime() - d.getTime()
  if (e.statut === 'assemble')
    return e.rangee || (e.assembleLe !== null && age(e.assembleLe) >= CONSERVATION_ASSEMBLE_MS)
  if (e.statut === 'echec') return age(e.recuLe) >= CONSERVATION_ECHEC_MS
  return false
}
